import React, { useState, useCallback, memo } from 'react'
import Box from '@/primitives/Box'
import FlexV2 from '@/primitives/FlexV2'
import Text from '@/primitives/Text'
import Button from '@/primitives/Button'
import Strings from '../../../Strings'
import { SectionHeader, AnimatedMessage } from './shared'
import { I18n } from 'aws-amplify'
import useIsRtl from '@/hooks/useIsRtl'

const ExecutiveStrings = () => ({
  executiveSummaryTitle: I18n.get(
    'Executive Summary: Red Cherry Tomato (Aida) Trial'
  ),
  summaryIntro: I18n.get(
    'The iyris SecondSky roof vs. standard plastic trial for tomatoes is showing promising results'
  ),
  milestone1Part1: I18n.get(
    'Early growth saw thriving seedlings with low mortality by'
  ),
  milestone1Date: I18n.get('January 18, 2025'),

  milestone2Part1: I18n.get('By'),
  milestone2Date: I18n.get('February 10, 2025'),
  milestone2Part2: I18n.get(
    'iyris plants had shorter internodes, earlier flowering, and thicker stems'
  ),

  milestone3Part1: I18n.get('Plant growth was'),
  milestone3Percent: I18n.get('5-10%'),
  milestone3Part2: I18n.get('more condensed under iyris by'),
  milestone3Date: I18n.get('February 16, 2025'),

  milestone4Part1: I18n.get('First tomato harvest from the iyris side on'),
  milestone4Date: I18n.get('February 24, 2025'),

  milestone5: I18n.get('Interestingly, iyris needed no extra shading to date'),

  milestone6Part1: I18n.get(
    'Nutrient analysis prompted altered fertigation under iyris by'
  ),
  milestone6Date: I18n.get('February 25, 2025'),

  milestone7Part1: I18n.get(
    'The first full week of yielding was completed in both by'
  ),
  milestone7Date: I18n.get('February 28, 2025'),

  summaryConclusion: I18n.get(
    'Early signs suggest the iyris SecondSky roof is impacting tomato development and environmental management favorably'
  )
})

const FormattedSummary = memo(() => {
  const executiveStrings = ExecutiveStrings()
  const isRTL = useIsRtl()

  return (
    <Box className='summary-content' dir={isRTL ? 'rtl' : 'ltr'}>
      <Text
        size={300}
        fontWeight={600}
        className='summary-title'
        dir={isRTL ? 'rtl' : 'ltr'}
      >
        {isRTL ? '🌱 🌱' : '🌱'} {executiveStrings.executiveSummaryTitle}{' '}
        {isRTL ? '' : '🌱'}
      </Text>
      <Box className='summary-paragraph'>
        <Text dir={isRTL ? 'rtl' : 'ltr'}>{executiveStrings.summaryIntro}</Text>
      </Box>
      <Box className='summary-section'>
        <Box className='milestone-list'>
          <Box className='milestone-item'>
            <Text dir={isRTL ? 'rtl' : 'ltr'}>
              {isRTL ? '' : '📅 '}
              {executiveStrings.milestone1Part1}{' '}
              <Text as='span' fontWeight={600}>
                {executiveStrings.milestone1Date}
              </Text>
              .{isRTL ? ' 📅' : ''}
            </Text>
          </Box>
          <Box className='milestone-item'>
            <Text dir={isRTL ? 'rtl' : 'ltr'}>
              {isRTL ? '' : '🍅 '}
              {executiveStrings.milestone2Part1}{' '}
              <Text as='span' fontWeight={600}>
                {executiveStrings.milestone2Date}
              </Text>
              , {executiveStrings.milestone2Part2}
              {isRTL ? ' 🍅' : ''}
            </Text>
          </Box>
          <Box className='milestone-item'>
            <Text dir={isRTL ? 'rtl' : 'ltr'}>
              {isRTL ? '' : '📏 '}
              {executiveStrings.milestone3Part1}{' '}
              <Text as='span' fontWeight={600}>
                {executiveStrings.milestone3Percent}
              </Text>{' '}
              {executiveStrings.milestone3Part2}{' '}
              <Text as='span' fontWeight={600}>
                {executiveStrings.milestone3Date}
              </Text>
              .{isRTL ? ' 📏' : ''}
            </Text>
          </Box>
          <Box className='milestone-item'>
            <Text dir={isRTL ? 'rtl' : 'ltr'}>
              {isRTL ? '' : '🥇 '}
              {executiveStrings.milestone4Part1}{' '}
              <Text as='span' fontWeight={600}>
                {executiveStrings.milestone4Date}
              </Text>
              !{isRTL ? ' 🥇' : ''}
            </Text>
          </Box>
          <Box className='milestone-item'>
            <Text dir={isRTL ? 'rtl' : 'ltr'}>
              {isRTL ? '' : '☀️ '}
              {executiveStrings.milestone5}
              {isRTL ? ' ☀️' : ''}
            </Text>
          </Box>
          <Box className='milestone-item'>
            <Text dir={isRTL ? 'rtl' : 'ltr'}>
              {isRTL ? '' : '💧 '}
              {executiveStrings.milestone6Part1}{' '}
              <Text as='span' fontWeight={600}>
                {executiveStrings.milestone6Date}
              </Text>
              .{isRTL ? ' 💧' : ''}
            </Text>
          </Box>
          <Box className='milestone-item'>
            <Text dir={isRTL ? 'rtl' : 'ltr'}>
              {isRTL ? '' : '📊 '}
              {executiveStrings.milestone7Part1}{' '}
              <Text as='span' fontWeight={600}>
                {executiveStrings.milestone7Date}
              </Text>
              .{isRTL ? ' 📊' : ''}
            </Text>
          </Box>
        </Box>
      </Box>
      <Box className='summary-conclusion'>
        <Text dir={isRTL ? 'rtl' : 'ltr'}>
          {executiveStrings.summaryConclusion}
        </Text>
      </Box>
    </Box>
  )
})

const ExecutiveSummary = memo(() => {
  const [generating, setGenerating] = useState(false)
  const [showSummary, setShowSummary] = useState(false)
  const strings = Strings()
  const isRTL = useIsRtl()

  const handleGenerateSummary = useCallback(() => {
    setGenerating(true)
    setShowSummary(false)
    setTimeout(() => {
      setShowSummary(true)
      setGenerating(false)
    }, 2000)
  }, [])

  return (
    <Box className='executive-summary' dir={isRTL ? 'rtl' : 'ltr'}>
      <FlexV2 direction='column' axisGap={200}>
        <SectionHeader
          icon='summarize'
          title={strings.executiveSummary}
          dir={isRTL ? 'rtl' : 'ltr'}
        />

        {!showSummary && !generating && (
          <Button
            variant='primary'
            iconBefore={isRTL ? null : 'auto_awesome'}
            iconAfter={isRTL ? 'auto_awesome' : null}
            onClick={handleGenerateSummary}
            dir={isRTL ? 'rtl' : 'ltr'}
          >
            {strings.generateExecutiveSummary}
          </Button>
        )}

        {generating && (
          <AnimatedMessage
            message={strings.analyzingTrialData}
            status='processing'
            dir={isRTL ? 'rtl' : 'ltr'}
          />
        )}

        {showSummary && (
          <>
            <FormattedSummary />
            <Button
              variant='outline'
              iconBefore={isRTL ? null : 'refresh'}
              iconAfter={isRTL ? 'refresh' : null}
              onClick={handleGenerateSummary}
              className='regenerate-button'
              tone={600}
              dir={isRTL ? 'rtl' : 'ltr'}
            >
              {strings.regenerateSummary}
            </Button>
          </>
        )}
      </FlexV2>
    </Box>
  )
})

export default ExecutiveSummary
